import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import type { Song } from '../../shared/api/types';
import { Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

/** Biblioteca de cantos de Alabanza. */
export function SongsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState('');
  const data = useAsync(
    (signal) => (churchId ? api.get<{ items: Song[]; canEdit: boolean }>(`/worship/churches/${churchId}/songs?archived=${archived}`, signal) : Promise.resolve({ items: [], canEdit: false })),
    [churchId, archived],
  );
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  const needle = q.trim().toLocaleLowerCase('es');
  const items = data.status === 'ready' ? data.data.items.filter((s) => !needle || `${s.title} ${s.author ?? ''} ${s.lyrics}`.toLocaleLowerCase('es').includes(needle)) : [];
  return (
    <div className="page">
      <header className="page__header page__header--actions">
        <div>
          <h1>Cantos</h1>
          <p className="muted">La biblioteca de Alabanza: letra, tono y BPM. De aquí salen los cantos del culto y las letras de Presentación.</p>
        </div>
        {data.status === 'ready' && data.data.canEdit ? (
          <Link className="btn btn--primary" to="/app/cantos/nuevo">
            Nuevo canto
          </Link>
        ) : null}
      </header>
      <div className="tabs" role="tablist" aria-label="Cantos">
        {([[false, 'Vigentes'], [true, 'Archivados']] as const).map(([value, label]) => (
          <button key={label} type="button" role="tab" aria-selected={archived === value} className={`tabs__tab${archived === value ? ' tabs__tab--active' : ''}`} onClick={() => setArchived(value)}>
            {label}
          </button>
        ))}
      </div>
      <div className="inline-form">
        <label className="visually-hidden" htmlFor="song-q">
          Buscar canto
        </label>
        <input id="song-q" type="search" placeholder="Buscar por título, autor o letra…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && items.length === 0 ? (
        <Card>
          <EmptyState title={q ? 'Ningún canto coincide.' : archived ? 'No hay cantos archivados.' : 'Todavía no hay cantos.'} />
        </Card>
      ) : null}
      {items.length > 0 ? (
        <ul className="list">
          {items.map((s) => (
            <li key={s.id} className="list__item">
              <span className="agenda__body">
                <Link to={`/app/cantos/${s.id}`}>
                  <strong>{s.title}</strong>
                </Link>
                <span className="muted small">{s.author ?? 'Autor sin registrar'}</span>
              </span>
              <span className="song-meta">
                {s.key ? <span className="badge">Tono {s.key}</span> : null}
                {s.bpm ? <span className="badge">{s.bpm} BPM</span> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
